import React, { useMemo, memo, Profiler } from 'react';
//import { useCardToggle } from 'context/CardCenterContext';
import { useComponentToggle } from 'context/ComponentToggleContext';
import useAttention from 'context/AttentionContext';
import CardTool from 'Components/Card/CardTool';
import RefreshLink from 'Components/RefreshLink';
import * as Icons from '@ace/react-icons';
import useUTag from 'tracking/useUTag';
import onRenderCallback from 'tracking/onRenderCallback';
import { unstable_trace as trace } from 'scheduler/tracing';
import styles from 'assets/css/Card.module.css';
import messageStyles from 'assets/css/Message.module.css';
import buttonStyles from 'assets/css/Button.module.css';
import * as Sentry from "@sentry/react";

const enableDevLinks = process.env.REACT_APP_ENABLE_DEV_LINKS === 'true';

const CardHeader = (props) => {
    const iconName = props.cardIconKey && `${props.cardIconKey}Icon`;

    const CardTitleIcon =  useMemo(() => {
        const icon = Icons[iconName];

        if (iconName && !icon) {
            Sentry.captureException(new Error(`No CardHeaderIcon exists for ${iconName}.  Card.ComponentName: '${props.componentName}'`));
        }
        return icon;
    }, [iconName, props.componentName]);
    const hasIcon = CardTitleIcon ? true : false;

    return (
        <header className={styles.cardHeader}>
            {useMemo(
                () => (
                    <>
                        <div className={styles.titleWrap}>
                            {hasIcon && <CardTitleIcon className={styles.titleIcon} />}
                            <h4 className={`${styles.titleText} ${styles.titleTextLhPlus}`}>
                                {props.cardTitle}
                            </h4>
                        </div>

                        {enableDevLinks && <CardDevLinks componentName={props.componentName} />}
                        <div className={styles.toolsWrap}>
                            {props.cardTools && <CardTools tools={props.cardTools} />}
                            {!props.hideCollapse && <CardCollapse componentName={props.componentName} />}
                            {props.draggable && <DragHandle />}
                        </div>
                    </>
                ),
                [props.cardTitle, props.cardTools, props.draggable, props.componentName, hasIcon]
            )}
        </header>
    )
}

const CardCollapse = memo(
    ({componentName}) => {
        const { toggleComponent } = useComponentToggle(componentName);

        return (
            <i className={`${styles.toolIconWrap} ${styles.collapseIconWrap}`} onClick={() => { toggleComponent(); }} title="Expand/Collapse Card">
                <Icons.IconAngleDownBold fill="#b5b9bc" />
            </i>
        );
    }
);

const CardDevLinks = memo(
    ({componentName}) => {
        const AttentionStateLinks = useMemo(() => (<CardHeaderAttentionStateLinks />), []);

        return (
            <div className={buttonStyles.cardRefreshLink}>
                {AttentionStateLinks}
                <RefreshLink componentName={componentName} />
            </div>
        );
    }
);

const DragHandle = memo(
    () => (
        <i className={`${styles.toolIconWrap} ${styles.dragIconWrap} drag-handle`} title="Drag">
            <Icons.IconDrag />
        </i>
    )
);

const CardTools = memo(
    ({tools}) => (
        <div className={`${styles.cardTools} ${styles.toolsWrap}`}>
            {tools.map((tool) => (<CardTool key={tool.ToolType + tool.ToolIcon} tool={tool} />))}
        </div>
    )
);

const CardHeaderAttentionStateLinks = () => {
    const {
        setAttentionInfo,
        setAttentionWarning,
        setAttentionAlert,
        clearAttentionState
    } = useAttention();

    return trace('render CardHeaderAttentionStateLinks', performance.now(), () => (
        <Profiler id="CardHeaderAttentionStateLinks" onRender={onRenderCallback}>
            <AttentionStateLink onClickEvent={setAttentionInfo} name={"info"} />
            <AttentionStateLink onClickEvent={setAttentionWarning} name={"warning"} />
            <AttentionStateLink onClickEvent={setAttentionAlert} name={"alert"} />
            <AttentionStateLink onClickEvent={clearAttentionState} name={"clear"} />
        </Profiler>
    ));
}

const AttentionStateLink = ({onClickEvent, name}) => {
    const { linkEvent } = useUTag();

    return (
        <a className={messageStyles.link} onClick={() => linkEvent({ event: "click-attention-state", "attention-state": name }, () => onClickEvent())} style={{cursor: "pointer", paddingLeft: ".25rem", paddingRight: ".25rem"}}>
            [{name}]
        </a>
    );
}

export default CardHeader;
